import { Link, useNavigate } from "@tanstack/react-router";

import { useActiveLanguage } from "./activeLanguage";
import { getLanguage } from "./languages";

/**
 * The landing every route falls back to when the active language has
 * no seeded content yet. Honest UI instead of fake empty progress bars.
 *
 * Visible behaviour today: pick Italian / Portuguese / Greek / Latin
 * from the switcher → every page renders this. German never sees it.
 * When Phase 2 seeds Italian/Portuguese, flip `available: true` in
 * `lib/languages.ts` and the same gate turns the dashboards back on.
 *
 * "Back to German" both flips the active language AND navigates to the
 * home page — a fresh dashboard is the cleanest reset and avoids the
 * stale-data edge cases that would come from staying on the current
 * route after a language change.
 */
export function UnseededLanguageLanding() {
  const { language, setLanguage } = useActiveLanguage();
  const navigate = useNavigate();
  const meta = getLanguage(language);
  if (!meta) return null;

  const backToGerman = () => {
    setLanguage("de");
    navigate({ to: "/" });
  };

  return (
    <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 sm:py-24 text-center">
      {/* Frontispiece — the native-script name as a hero, with the
          ISO code as a colophon underneath. A book cover, not a 404. */}
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-400">
        {meta.glyph} · Phase 2
      </div>
      <div className="font-serif text-5xl sm:text-6xl text-ink-900 tracking-tight mb-3 leading-none">
        {meta.nativeName}
      </div>
      <div className="eyebrow mb-8">{meta.englishName}</div>
      <div className="mx-auto mb-8 h-px w-16 bg-ink-200" />
      <h1 className="font-serif text-2xl sm:text-3xl text-ink-900 mb-4 tracking-tight">
        Not seeded yet
      </h1>
      <p className="text-ink-500 mb-8 leading-relaxed max-w-lg mx-auto">
        Phase 1 of lingua is the German visa track — only German has a populated
        curriculum today.{" "}
        <span className="text-ink-900">{meta.englishName}</span> content is on
        the roadmap for Phase 2 (post-November 2026). The picker lists every
        planned language so the switching machinery works end-to-end, but the
        dashboards stay empty until the seed lands.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          onClick={backToGerman}
          className="bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
        >
          Back to German
        </button>
        <Link
          to="/about"
          className="border border-ink-200 px-5 py-3 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
        >
          How lingua works
        </Link>
      </div>
    </section>
  );
}
